import React, { useState, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShieldAlt,
  faExclamationTriangle,
  faGlobe,
  faUser,
  faChartLine,
  faBan,
  faSearch,
  faMapMarkerAlt,
  faClock,
  faExclamationCircle,
} from "@fortawesome/free-solid-svg-icons";
import RefreshButton from "../../common/RefreshButton";
import LoadingSpinner from "../../common/LoadingSpinner";
import "./css/AuthenticationFailedAttempts.css";
import AuthenticationFailedAttemptsAPI from "../../services/AuthenticationFailedAttemptsAPI";
import { formatDateTime } from "../../utils/DateTimeUtil";

function AuthenticationFailedAttempts() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [failedAttempts, setFailedAttempts] = useState([]);
  const [suspiciousIps, setSuspiciousIps] = useState([]);
  const [failureStats, setFailureStats] = useState([]);
  const [selectedTimeRange, setSelectedTimeRange] = useState(24);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(10); // Changed from 20 to 10

  // Drill-down state
  const [showDetailedView, setShowDetailedView] = useState(false);
  const [detailedAttempts, setDetailedAttempts] = useState([]);
  const [detailedLoading, setDetailedLoading] = useState(false);
  const [detailedCurrentPage, setDetailedCurrentPage] = useState(0);
  const [detailedPageSize] = useState(10); // Changed from 50 to 10

  // Load all data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [dashboard, attempts, suspicious, stats] = await Promise.all([
        AuthenticationFailedAttemptsAPI.getDashboard(selectedTimeRange),
        AuthenticationFailedAttemptsAPI.getFailedAttempts(
          currentPage,
          pageSize,
          "attemptTimestamp",
          "desc",
          selectedTimeRange,
        ),
        AuthenticationFailedAttemptsAPI.getSuspiciousIps(selectedTimeRange),
        AuthenticationFailedAttemptsAPI.getFailureStats(selectedTimeRange),
      ]);

      setDashboardData({ ...dashboard, status: "NORMAL" });
      setFailedAttempts(attempts);
      setSuspiciousIps(suspicious);
      setFailureStats(stats);
    } catch (err) {
      console.error("Error loading authentication failed attempts data:", err);
      setError("Failed to load authentication security data");
    } finally {
      setLoading(false);
    }
  }, [selectedTimeRange, currentPage, pageSize]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleTimeRangeChange = (hours) => {
    setSelectedTimeRange(hours);
    setCurrentPage(0);
  };

  const handleFilterTypeChange = (newFilterType) => {
    setFilterType(newFilterType);

    // If "All Attempts" is selected, automatically reset and load all data
    if (newFilterType === "all") {
      setSearchTerm("");
      setCurrentPage(0);
      loadData();
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      loadData();
      return;
    }

    try {
      setLoading(true);
      setError(null);

      let attempts;
      if (filterType === "email") {
        attempts = await AuthenticationFailedAttemptsAPI.getAttemptsByEmail(
          searchTerm,
          currentPage,
          pageSize,
          selectedTimeRange,
        );
      } else if (filterType === "ip") {
        attempts = await AuthenticationFailedAttemptsAPI.getAttemptsByIp(
          searchTerm,
          currentPage,
          pageSize,
          selectedTimeRange,
        );
      } else {
        loadData();
        return;
      }

      // Update the attempts table
      setFailedAttempts(attempts);

      // Create derived dashboard data from search results
      const searchResults = attempts.content || attempts;
      const derivedDashboard = {
        totalAttempts: attempts.totalElements || searchResults.length,
        suspiciousIPs: 0, // Will be calculated below
        status: "FILTERED", // Indicate this is filtered data
      };

      // Calculate suspicious IPs from search results
      const uniqueIPs = new Set();
      const ipCounts = new Map();

      searchResults.forEach((attempt) => {
        uniqueIPs.add(attempt.ipAddress);
        ipCounts.set(
          attempt.ipAddress,
          (ipCounts.get(attempt.ipAddress) || 0) + 1,
        );
      });

      // Consider IPs with multiple attempts as suspicious
      const suspiciousIpsList = Array.from(ipCounts.entries())
        .filter(([ip, count]) => count > 1)
        .map(([ip, count]) => [ip, 1, count]); // [ip, uniqueEmails, totalAttempts]

      derivedDashboard.suspiciousIPs = suspiciousIpsList.length;

      // Calculate failure stats from search results
      const failureReasonCounts = new Map();
      searchResults.forEach((attempt) => {
        const reason = attempt.failureReason;
        failureReasonCounts.set(
          reason,
          (failureReasonCounts.get(reason) || 0) + 1,
        );
      });

      const derivedFailureStats = Array.from(
        failureReasonCounts.entries(),
      ).sort((a, b) => b[1] - a[1]); // Sort by count descending

      // Update state with derived data
      setDashboardData(derivedDashboard);
      setSuspiciousIps(suspiciousIpsList);
      setFailureStats(derivedFailureStats);
    } catch (err) {
      console.error("Search error:", err);
      setError("Failed to search authentication attempts");
    } finally {
      setLoading(false);
    }
  };

  // Handle drill-down to detailed view
  const handleTotalAttemptsClick = async () => {
    try {
      setDetailedLoading(true);
      setShowDetailedView(true);

      // Use getFailedAttempts with time filtering
      const detailedData =
        await AuthenticationFailedAttemptsAPI.getFailedAttempts(
          detailedCurrentPage,
          detailedPageSize,
          "attemptTimestamp",
          "desc",
          selectedTimeRange,
        );

      setDetailedAttempts(detailedData);
    } catch (error) {
      console.error("Failed to load detailed attempts:", error);
      setError("Failed to load detailed failed attempts");
    } finally {
      setDetailedLoading(false);
    }
  };

  // Handle closing detailed view
  const handleCloseDetailedView = () => {
    setShowDetailedView(false);
    setDetailedAttempts([]);
    setDetailedCurrentPage(0);
  };

  // Handle going back to summary view
  const handleBackToSummary = () => {
    setShowDetailedView(false);
    setDetailedAttempts([]);
    setDetailedCurrentPage(0);
  };

  const getThreatLevelColor = (score) => {
    if (score >= 75) return "#dc2626"; // red
    if (score >= 50) return "#ea580c"; // orange
    if (score >= 25) return "#d97706"; // amber
    return "#65a30d"; // green
  };

  const getThreatLevelText = (score) => {
    if (score >= 75) return "Critical";
    if (score >= 50) return "High";
    if (score >= 25) return "Medium";
    return "Low";
  };

  const formatFailureReason = (reason) => {
    return reason
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const renderDashboardSummary = () => {
    if (!dashboardData) return null;

    const isFiltered = filterType !== "all" && searchTerm.trim();

    return (
      <div className="failed-attempts-summary">
        {isFiltered && (
          <div className="search-filter-indicator">
            <FontAwesomeIcon icon={faSearch} />
            <span>
              Filtered by {filterType}: "{searchTerm}" • Last{" "}
              {selectedTimeRange} hours
            </span>
            <button
              onClick={() => {
                setSearchTerm("");
                setFilterType("all");
                loadData();
              }}
              className="clear-filter-btn"
              title="Clear search filter"
            >
              Clear Filter
            </button>
          </div>
        )}
        <div className="summary-cards">
          <div
            className="summary-card primary clickable"
            onClick={handleTotalAttemptsClick}
            style={{ cursor: "pointer" }}
            title="Click to view detailed attempts"
          >
            <div className="card-icon">
              <FontAwesomeIcon icon={faExclamationTriangle} />
            </div>
            <div className="card-content">
              <h3>Total Attempts</h3>
              <div className="card-value">
                {dashboardData.totalAttempts || 0}
              </div>
              <div className="card-subtitle">
                {dashboardData.status === "FILTERED"
                  ? "Filtered results • Click for details"
                  : `Last ${selectedTimeRange} hours • Click for details`}
              </div>
            </div>
          </div>

          <div className="summary-card warning">
            <div className="card-icon">
              <FontAwesomeIcon icon={faGlobe} />
            </div>
            <div className="card-content">
              <h3>Suspicious IPs</h3>
              <div className="card-value">
                {dashboardData.suspiciousIps?.length || 0}
              </div>
              <div className="card-subtitle">
                {dashboardData.status === "FILTERED"
                  ? "From filtered results"
                  : "Active threats detected"}
              </div>
            </div>
          </div>

          <div className="summary-card top-failures-card">
            <div className="card-icon">
              <FontAwesomeIcon icon={faChartLine} />
            </div>
            <div className="card-content">
              <h3>Top 3 Failure Reasons</h3>
              <div className="top-failures-list">
                {failureStats && failureStats.length > 0 ? (
                  failureStats.slice(0, 3).map((stat, index) => (
                    <div key={index} className="failure-reason-item">
                      <span className="failure-rank">#{index + 1}</span>
                      <span className="failure-reason">
                        {formatFailureReason(stat[0])}
                      </span>
                      <span className="failure-count">{stat[1]}</span>
                    </div>
                  ))
                ) : (
                  <div className="no-failure-data">No data available</div>
                )}
              </div>
              <div className="card-subtitle">Most common reasons</div>
            </div>
          </div>

          <div
            className={`summary-card ${
              dashboardData.status === "CRITICAL"
                ? "critical"
                : dashboardData.status === "WARNING"
                  ? "warning"
                  : "healthy"
            }`}
          >
            <div className="card-icon">
              <FontAwesomeIcon icon={faShieldAlt} />
            </div>
            <div className="card-content">
              <h3>Security Status</h3>
              <div className="card-value-text">{dashboardData.status}</div>
              <div className="card-subtitle">Overall threat level</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderControls = () => (
    <div className="failed-attempts-controls">
      <div className="controls-row">
        <div className="time-range-selector">
          <label>Time Range:</label>
          <select
            value={selectedTimeRange}
            onChange={(e) => handleTimeRangeChange(Number(e.target.value))}
          >
            <option value={1}>Last Hour</option>
            <option value={6}>Last 6 Hours</option>
            <option value={24}>Last 24 Hours</option>
            <option value={168}>Last Week</option>
          </select>
        </div>

        <div className="search-controls">
          <select
            value={filterType}
            onChange={(e) => handleFilterTypeChange(e.target.value)}
          >
            <option value="all">All Attempts</option>
            <option value="email">Search by Email</option>
            <option value="ip">Search by IP</option>
          </select>

          {filterType !== "all" && (
            <>
              <input
                type="text"
                placeholder={
                  filterType === "email"
                    ? "Enter email address..."
                    : "Enter IP address..."
                }
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleSearch()}
              />
              <button onClick={handleSearch} className="search-btn">
                <FontAwesomeIcon icon={faSearch} />
              </button>
            </>
          )}
        </div>

        <RefreshButton onClick={loadData} />
      </div>
    </div>
  );

  const renderSuspiciousIps = () => {
    if (!suspiciousIps || suspiciousIps.length === 0) {
      return null;
    }

    return (
      <div className="suspicious-ips-section">
        <h3>
          <FontAwesomeIcon icon={faGlobe} />
          Suspicious IP Addresses
        </h3>
        <div className="suspicious-ips-grid">
          {suspiciousIps.map((ip, index) => (
            <div key={index} className="suspicious-ip-card">
              <div className="ip-header">
                <span className="ip-address">{ip[0]}</span>
                <span className="threat-badge critical">High Risk</span>
              </div>
              <div className="ip-details">
                <div className="ip-stat">
                  <span className="stat-label">Unique Emails:</span>
                  <span className="stat-value">{ip[1]}</span>
                </div>
                <div className="ip-stat">
                  <span className="stat-label">Total Attempts:</span>
                  <span className="stat-value">{ip[2]}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderFailedAttempts = () => {
    if (!failedAttempts || failedAttempts.content?.length === 0) {
      return (
        <div className="no-data">
          <FontAwesomeIcon icon={faShieldAlt} />
          <p>No failed authentication attempts found</p>
        </div>
      );
    }

    const attempts = failedAttempts.content || failedAttempts;

    return (
      <div className="failed-attempts-table-section">
        <h3>
          <FontAwesomeIcon icon={faExclamationCircle} />
          Recent Failed Attempts
        </h3>
        <div className="attempts-table-container">
          <table className="attempts-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Email</th>
                <th>IP Address</th>
                <th>Failure Reason</th>
                <th>Threat Score</th>
                <th>Location</th>
                <th>Browser Fingerprint</th>
              </tr>
            </thead>
            <tbody>
              {attempts.map((attempt) => (
                <tr key={attempt.attemptId}>
                  <td>
                    <div className="time-cell">
                      <FontAwesomeIcon icon={faClock} />
                      {formatDateTime(attempt.attemptTimestamp)}
                    </div>
                  </td>
                  <td>
                    <div className="email-cell">
                      <FontAwesomeIcon icon={faUser} />
                      {attempt.emailAttempted}
                    </div>
                  </td>
                  <td>
                    <div className="ip-cell">
                      <FontAwesomeIcon icon={faGlobe} />
                      {attempt.ipAddress}
                      {attempt.isBlockedIp && (
                        <span className="blocked-badge">
                          <FontAwesomeIcon icon={faBan} />
                          Blocked
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="reason-cell">
                      {formatFailureReason(attempt.failureReason)}
                    </div>
                  </td>
                  <td>
                    <div
                      className="threat-score-cell"
                      style={{
                        color: getThreatLevelColor(attempt.threatScore),
                      }}
                    >
                      <span className="threat-score">
                        {attempt.threatScore}
                      </span>
                      <span className="threat-level">
                        {getThreatLevelText(attempt.threatScore)}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div className="location-cell">
                      {attempt.countryCode && (
                        <>
                          <FontAwesomeIcon icon={faMapMarkerAlt} />
                          {attempt.countryCode}
                        </>
                      )}
                      {attempt.geolocation && (
                        <span className="geolocation">
                          {attempt.geolocation}
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="fingerprint-cell">
                      {attempt.browserFingerprint ? (
                        <span
                          className="fingerprint-hash"
                          title={attempt.browserFingerprint}
                        >
                          {attempt.browserFingerprint.substring(0, 12)}...
                        </span>
                      ) : (
                        <span className="no-fingerprint">N/A</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="failed-attempts-loading">
        <LoadingSpinner message="Loading authentication security data..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="failed-attempts-error">
        <FontAwesomeIcon icon={faExclamationTriangle} />
        <h3>Error Loading Data</h3>
        <p>{error}</p>
        <button onClick={loadData} className="retry-btn">
          Retry
        </button>
      </div>
    );
  }

  // Render detailed attempts table for summary page
  const renderDetailedAttemptsTable = () => {
    return (
      <div className="failed-attempts-details">
        <div className="table-header">
          <h3>
            <FontAwesomeIcon icon={faExclamationTriangle} />
            Recent Failed Authentication Attempts
          </h3>
          <div className="table-info">
            <span className="time-range-info">
              Last {selectedTimeRange} hours
            </span>
            <div className="table-stats">
              <span className="stat-item">
                <strong>Total:</strong> {dashboardData?.totalAttempts || 0}
              </span>
              <span className="stat-item">
                <strong>Suspicious IPs:</strong>{" "}
                {dashboardData?.suspiciousIPs || 0}
              </span>
            </div>
          </div>
        </div>

        <div className="attempts-table-container">
          {loading ? (
            <div className="loading-spinner">
              <LoadingSpinner
                size="sm"
                message="Loading attempts..."
                className="compact"
              />
            </div>
          ) : failedAttempts.length === 0 ? null : (
            <>
              <table className="attempts-table-readable">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Email</th>
                    <th>IP Address</th>
                    <th>Location</th>
                    <th>Browser Fingerprint</th>
                    <th>Failure Reason</th>
                    <th>Threat Score</th>
                  </tr>
                </thead>
                <tbody>
                  {failedAttempts.content?.map((attempt) => (
                    <tr key={attempt.attemptId}>
                      <td>
                        <div className="time-cell-readable">
                          <span>
                            {formatDateTime(attempt.attemptTimestamp)}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="email-cell-readable">
                          <span>{attempt.emailAttempted}</span>
                        </div>
                      </td>
                      <td>
                        <div className="ip-cell-readable">
                          <span>{attempt.ipAddress}</span>
                          {attempt.isBlockedIp && (
                            <span className="blocked-badge-readable">
                              Blocked
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="location-cell-readable">
                          {attempt.countryCode && (
                            <span>{attempt.countryCode}</span>
                          )}
                          {attempt.geolocation && (
                            <span className="geolocation-readable">
                              {attempt.geolocation}
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="fingerprint-cell-readable">
                          {attempt.browserFingerprint ? (
                            <span
                              className="fingerprint-hash-readable"
                              title={attempt.browserFingerprint}
                            >
                              {attempt.browserFingerprint.substring(0, 12)}...
                            </span>
                          ) : (
                            <span className="no-fingerprint-readable">N/A</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="reason-cell-readable">
                          <span className="failure-reason-badge">
                            {formatFailureReason(attempt.failureReason)}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="threat-score-cell-readable">
                          <span
                            className={`threat-score-badge threat-${getThreatLevelText(
                              attempt.threatScore,
                            ).toLowerCase()}`}
                          >
                            {attempt.threatScore}
                          </span>
                          <span className="threat-level-text">
                            {getThreatLevelText(attempt.threatScore)}
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination controls */}
              {failedAttempts.totalPages > 1 && (
                <div className="pagination-controls">
                  <button
                    className="pagination-btn"
                    onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                    disabled={currentPage === 0}
                  >
                    Previous
                  </button>

                  <span className="pagination-info">
                    Page {currentPage + 1} of {failedAttempts.totalPages} (
                    {failedAttempts.totalElements} total entries)
                  </span>

                  <button
                    className="pagination-btn"
                    onClick={() =>
                      setCurrentPage(
                        Math.min(
                          failedAttempts.totalPages - 1,
                          currentPage + 1,
                        ),
                      )
                    }
                    disabled={currentPage >= failedAttempts.totalPages - 1}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  // Render detailed attempts view inline
  const renderDetailedView = () => {
    if (!showDetailedView) return null;

    return (
      <div className="detailed-view-inline">
        <div className="detailed-view-header-inline">
          <div className="header-left">
            <button
              className="back-btn"
              onClick={handleBackToSummary}
              title="Back to summary"
            >
              ← Back to Summary
            </button>
            <h3>
              <FontAwesomeIcon icon={faExclamationTriangle} />
              Failed Authentication Attempts Details
            </h3>
          </div>
          <div className="detailed-view-info-inline">
            <p>
              Showing all failed attempts for the last {selectedTimeRange} hours
            </p>
            <div className="detailed-stats-inline">
              <span className="stat">
                <strong>Total:</strong> {dashboardData?.totalAttempts || 0}
              </span>
              <span className="stat">
                <strong>Suspicious IPs:</strong>{" "}
                {dashboardData?.suspiciousIPs || 0}
              </span>
            </div>
          </div>
        </div>

        <div className="detailed-view-content-inline">
          {detailedLoading ? (
            <div className="loading-spinner">
              <LoadingSpinner
                size="sm"
                message="Loading detailed attempts..."
                className="compact"
              />
            </div>
          ) : detailedAttempts.length === 0 ? (
            <div className="no-data">
              <FontAwesomeIcon icon={faExclamationCircle} />
              <p>No failed attempts found for the selected time period.</p>
            </div>
          ) : (
            <div className="detailed-attempts-table-inline">
              <table className="attempts-table-inline">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Email</th>
                    <th>IP Address</th>
                    <th>Location</th>
                    <th>Browser Fingerprint</th>
                    <th>Failure Reason</th>
                    <th>Threat Score</th>
                  </tr>
                </thead>
                <tbody>
                  {detailedAttempts.content?.map((attempt) => (
                    <tr key={attempt.attemptId}>
                      <td>
                        <div className="time-cell">
                          <FontAwesomeIcon icon={faClock} />
                          {formatDateTime(attempt.attemptTimestamp)}
                        </div>
                      </td>
                      <td>
                        <div className="email-cell">
                          <FontAwesomeIcon icon={faUser} />
                          {attempt.emailAttempted}
                        </div>
                      </td>
                      <td>
                        <div className="ip-cell">
                          <FontAwesomeIcon icon={faGlobe} />
                          {attempt.ipAddress}
                          {attempt.isBlockedIp && (
                            <span className="blocked-badge">
                              <FontAwesomeIcon icon={faBan} />
                              Blocked
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="location-cell">
                          {attempt.countryCode && (
                            <>
                              <FontAwesomeIcon icon={faMapMarkerAlt} />
                              {attempt.countryCode}
                            </>
                          )}
                          {attempt.geolocation && (
                            <span className="geolocation">
                              {attempt.geolocation}
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="fingerprint-cell">
                          {attempt.browserFingerprint ? (
                            <span
                              className="fingerprint-hash"
                              title={attempt.browserFingerprint}
                            >
                              {attempt.browserFingerprint.substring(0, 12)}...
                            </span>
                          ) : (
                            <span className="no-fingerprint">N/A</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="reason-cell">
                          {formatFailureReason(attempt.failureReason)}
                        </div>
                      </td>
                      <td>
                        <div
                          className="threat-score-cell"
                          style={{
                            color: getThreatLevelColor(attempt.threatScore),
                          }}
                        >
                          <span className="threat-score">
                            {attempt.threatScore}
                          </span>
                          <span className="threat-level">
                            {getThreatLevelText(attempt.threatScore)}
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination controls for detailed view */}
              {detailedAttempts.totalPages > 1 && (
                <div className="pagination-controls">
                  <button
                    className="pagination-btn"
                    onClick={() =>
                      setDetailedCurrentPage(
                        Math.max(0, detailedCurrentPage - 1),
                      )
                    }
                    disabled={detailedCurrentPage === 0}
                  >
                    Previous
                  </button>

                  <span className="pagination-info">
                    Page {detailedCurrentPage + 1} of{" "}
                    {detailedAttempts.totalPages} (
                    {detailedAttempts.totalElements} total entries)
                  </span>

                  <button
                    className="pagination-btn"
                    onClick={() =>
                      setDetailedCurrentPage(
                        Math.min(
                          detailedAttempts.totalPages - 1,
                          detailedCurrentPage + 1,
                        ),
                      )
                    }
                    disabled={
                      detailedCurrentPage >= detailedAttempts.totalPages - 1
                    }
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="authentication-failed-attempts">
      {renderControls()}
      {renderDashboardSummary()}

      <div className="content-grid">
        <div className="main-content">
          {renderSuspiciousIps()}
          {renderDetailedAttemptsTable()}
        </div>
      </div>
    </div>
  );
}

export default AuthenticationFailedAttempts;
